(() => {
  'use strict';

  const liveRegion = () => document.getElementById('LiveRegion');
  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  class StoreHeader extends HTMLElement {
    connectedCallback() {
      this.menuButton = this.querySelector('[data-menu-toggle]');
      this.mobileMenu = this.querySelector('[data-mobile-menu]');
      this.searchButtons = this.querySelectorAll('[data-search-toggle]');
      this.searchOverlay = this.querySelector('[data-search-overlay]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.megaToggles = Array.from(this.querySelectorAll('[data-mega-toggle]'));
      this.megaItems = Array.from(this.querySelectorAll('[data-mega-item]'));

      this.menuButton?.addEventListener('click', () => this.toggleMenu());
      this.searchButtons.forEach((button) => button.addEventListener('click', () => this.openSearch(button)));
      this.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', () => this.closeSearch()));
      this.megaToggles.forEach((toggle) => toggle.addEventListener('click', () => this.toggleMega(toggle)));
      document.addEventListener('click', (event) => {
        if (!this.megaItems.some((item) => item.contains(event.target))) this.closeMegas();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closeMenu();
        this.closeSearch();
        this.closeMegas();
      });
    }

    toggleMenu() {
      const willOpen = this.mobileMenu?.hasAttribute('hidden');
      if (willOpen) this.openMenu(); else this.closeMenu();
    }

    openMenu() {
      if (!this.mobileMenu || !this.menuButton) return;
      this.closeSearch();
      this.closeMegas();
      this.mobileMenu.removeAttribute('hidden');
      this.menuButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
    }

    closeMenu() {
      if (!this.mobileMenu || !this.menuButton || this.mobileMenu.hasAttribute('hidden')) return;
      this.mobileMenu.setAttribute('hidden', '');
      this.menuButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    }

    openSearch(button) {
      if (!this.searchOverlay) return;
      this.closeMenu();
      this.closeMegas();
      this.activeSearchButton = button?.closest('[data-mobile-menu]') ? this.querySelector('.ngb-exact-header__search-button') : button;
      this.searchOverlay.removeAttribute('hidden');
      button?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overlay-open');
      window.setTimeout(() => this.searchInput?.focus(), 80);
    }

    closeSearch() {
      if (!this.searchOverlay || this.searchOverlay.hasAttribute('hidden')) return;
      this.searchOverlay.setAttribute('hidden', '');
      this.activeSearchButton?.setAttribute('aria-expanded', 'false');
      this.searchInput?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-open');
      this.activeSearchButton?.focus();
    }

    toggleMega(toggle) {
      const item = toggle.closest('[data-mega-item]');
      if (!item) return;
      const willOpen = !item.classList.contains('is-open');
      this.closeMegas(item);
      item.classList.toggle('is-open', willOpen);
      toggle.setAttribute('aria-expanded', String(willOpen));
    }

    closeMegas(exceptItem = null) {
      this.megaItems.forEach((item) => {
        if (item === exceptItem) return;
        item.classList.remove('is-open');
        item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }
  }

  if (!customElements.get('store-header')) customElements.define('store-header', StoreHeader);

  class HeaderTrending extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-trending-item]'));
      if (!this.items.length) return;

      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.delay = Math.max(4000, Number.parseInt(this.dataset.interval, 10) || 5000);
      this.timer = null;
      this.pointerInside = false;
      this.focusInside = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.layoutQuery = window.matchMedia('(min-width: 1200px)');

      this.onPointerEnter = () => {
        this.pointerInside = true;
        this.stop();
      };
      this.onPointerLeave = () => {
        this.pointerInside = false;
        this.start();
      };
      this.onFocusIn = () => {
        this.focusInside = true;
        this.stop();
      };
      this.onFocusOut = () => {
        window.requestAnimationFrame(() => {
          this.focusInside = this.contains(document.activeElement);
          this.start();
        });
      };
      this.onVisibilityChange = () => this.start();
      this.onPreferenceChange = () => {
        if (this.motionQuery.matches) this.show(0);
        this.start();
      };

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery.addEventListener?.('change', this.onPreferenceChange);
      this.layoutQuery.addEventListener?.('change', this.onPreferenceChange);

      this.show(this.index);
      this.start();
    }

    show(index) {
      this.index = (index + this.items.length) % this.items.length;
      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === this.index;
        item.classList.toggle('is-active', isActive);
        if (isActive) {
          item.removeAttribute('aria-hidden');
          item.removeAttribute('tabindex');
        } else {
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
        }
      });
    }

    start() {
      this.stop();
      if (this.items.length < 2 || this.motionQuery.matches || !this.layoutQuery.matches || document.hidden || this.pointerInside || this.focusInside) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    disconnectedCallback() {
      this.stop();
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery?.removeEventListener?.('change', this.onPreferenceChange);
      this.layoutQuery?.removeEventListener?.('change', this.onPreferenceChange);
    }
  }

  if (!customElements.get('header-trending')) customElements.define('header-trending', HeaderTrending);

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.form = this.querySelector('form[role="search"]');
      this.initialMarkup = this.results?.innerHTML || '';
      this.abortController = null;
      this.timer = null;

      this.input?.addEventListener('input', () => this.queueSearch());
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          const firstResult = this.results?.querySelector('a');
          if (firstResult) {
            event.preventDefault();
            firstResult.focus();
          }
        }
      });
      this.form?.addEventListener('submit', () => this.remember(this.input?.value));
      this.addEventListener('click', (event) => {
        const suggestion = event.target.closest('[data-search-suggestion]');
        if (suggestion) this.remember(suggestion.textContent);
      });
      this.renderRecentSearches();
    }

    queueSearch() {
      window.clearTimeout(this.timer);
      const term = this.input?.value.trim() || '';
      if (term.length < 2) {
        this.abortController?.abort();
        if (this.results) this.results.innerHTML = this.initialMarkup;
        this.input?.setAttribute('aria-expanded', 'false');
        this.setStatus('');
        this.renderRecentSearches();
        return;
      }
      this.timer = window.setTimeout(() => this.search(term), 220);
    }

    async search(term) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setStatus(this.dataset.loadingText || 'Searching…');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}search/suggest?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,query&resources[limit]=6&resources[options][fields]=title,product_type,tag,vendor,variants.title&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { signal: this.abortController.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Predictive search request failed');
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        if (this.results) this.results.innerHTML = section?.innerHTML || text;
        this.input?.setAttribute('aria-expanded', 'true');
        const count = this.results?.querySelectorAll('.predictive-product').length || 0;
        this.setStatus(count ? `${count} product suggestions loaded.` : 'Search suggestions loaded.');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setStatus('Suggestions are unavailable. Press Enter to search.');
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    getRecentSearches() {
      try {
        const value = JSON.parse(window.localStorage.getItem('beautyraise_recent_searches') || '[]');
        return Array.isArray(value) ? value.filter((item) => typeof item === 'string').slice(0, 5) : [];
      } catch (error) {
        return [];
      }
    }

    remember(value) {
      const term = String(value || '').trim();
      if (term.length < 2) return;
      const searches = [term, ...this.getRecentSearches().filter((item) => item.toLowerCase() !== term.toLowerCase())].slice(0, 5);
      try { window.localStorage.setItem('beautyraise_recent_searches', JSON.stringify(searches)); } catch (error) {}
    }

    renderRecentSearches() {
      const wrapper = this.querySelector('[data-recent-searches]');
      const list = this.querySelector('[data-recent-search-list]');
      if (!wrapper || !list) return;
      const searches = this.getRecentSearches();
      wrapper.toggleAttribute('hidden', searches.length === 0);
      list.replaceChildren();
      const root = window.Shopify?.routes?.root || '/';
      searches.forEach((term) => {
        const link = document.createElement('a');
        link.href = `${root}search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        link.textContent = term;
        link.addEventListener('click', () => this.remember(term));
        list.appendChild(link);
      });
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  class WelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.emailInput = this.querySelector('[data-welcome-email]');
      this.copyButton = this.querySelector('[data-copy-discount]');
      this.storageKey = this.dataset.storageKey || 'ngb-welcome-popup';
      this.delay = Math.max(0, Number(this.dataset.delayMs) || 0);
      this.designMode = this.dataset.designMode === 'true';
      this.forceFromUrl = new URLSearchParams(window.location.search).get('show_offer') === '1';
      this.hasFormResult = Boolean(this.querySelector('[data-welcome-success], [data-welcome-error]'));
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onSectionSelect = (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open(true);
      };

      this.querySelectorAll('[data-welcome-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.copyButton?.addEventListener('click', () => this.copyCode());
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:section:select', this.onSectionSelect);

      if (this.designMode || this.hasFormResult || this.forceFromUrl) {
        this.timer = window.setTimeout(() => this.open(true), 100);
      } else if (this.shouldShow()) {
        this.timer = window.setTimeout(() => this.open(), this.delay);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.body.classList.remove('welcome-popup-open');
    }

    shouldShow() {
      try {
        return window.sessionStorage.getItem(this.storageKey) !== 'shown';
      } catch (error) {
        return true;
      }
    }

    markShown() {
      if (this.designMode) return;
      try { window.sessionStorage.setItem(this.storageKey, 'shown'); } catch (error) {}
    }

    open(force = false) {
      if (!force && document.body.matches('.menu-open, .overlay-open, .filter-open')) {
        this.timer = window.setTimeout(() => this.open(), 900);
        return;
      }
      if (!this.hasAttribute('hidden')) return;
      this.lastFocused = document.activeElement;
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('welcome-popup-open');
      this.markShown();
      window.setTimeout(() => (this.emailInput || this.querySelector('[data-welcome-close]'))?.focus(), 60);
    }

    close() {
      if (this.hasAttribute('hidden')) return;
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('welcome-popup-open');
      this.lastFocused?.focus?.();
    }

    handleKeydown(event) {
      if (this.hasAttribute('hidden')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.dialog) return;
      const focusable = Array.from(this.dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async copyCode() {
      const code = this.querySelector('[data-discount-code]')?.textContent.trim();
      if (!code || !this.copyButton) return;
      try {
        await window.navigator.clipboard.writeText(code);
      } catch (error) {
        const range = document.createRange();
        const codeElement = this.querySelector('[data-discount-code]');
        range.selectNodeContents(codeElement);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
      }
      this.copyButton.textContent = 'Copied';
      announce(`Discount code ${code} copied.`);
    }
  }

  if (!customElements.get('welcome-popup')) customElements.define('welcome-popup', WelcomePopup);

  class ProductRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.previousButton = this.querySelector('[data-rail-prev]');
      this.nextButton = this.querySelector('[data-rail-next]');
      if (!this.track) return;
      this.previousButton?.addEventListener('click', () => this.scroll(-1));
      this.nextButton?.addEventListener('click', () => this.scroll(1));
      this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.updateButtons()), { passive: true });
      window.addEventListener('resize', () => this.updateButtons(), { passive: true });
      this.updateButtons();
    }

    scroll(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.82, behavior: 'smooth' });
    }

    updateButtons() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth - 2);
      if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll;
    }
  }

  if (!customElements.get('product-rail')) customElements.define('product-rail', ProductRail);

  class ProductHeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.prioritizeAvailable === 'true') {
        const slideContainer = this.querySelector('.ngb-exact-carousel__slides');
        const orderedSlides = Array.from(this.querySelectorAll('[data-hero-slide]')).sort((first, second) => {
          const firstAvailable = first.dataset.productAvailable === 'true' ? 1 : 0;
          const secondAvailable = second.dataset.productAvailable === 'true' ? 1 : 0;
          return secondAvailable - firstAvailable;
        });
        orderedSlides.forEach((slide) => slideContainer?.appendChild(slide));
        orderedSlides.forEach((slide, index) => {
          slide.toggleAttribute('hidden', index !== 0);
          slide.classList.toggle('is-active', index === 0);
          slide.setAttribute('aria-hidden', String(index !== 0));
        });
      }
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.previousButton = this.querySelector('[data-hero-prev]');
      this.nextButton = this.querySelector('[data-hero-next]');
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.status = this.querySelector('[data-hero-status]');
      this.current = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = false;

      if (this.slides.length < 2) return;
      this.previousButton?.addEventListener('click', () => this.goTo(this.current - 1, true));
      this.nextButton?.addEventListener('click', () => this.goTo(this.current + 1, true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.heroDot), true)));
      this.pauseButton?.addEventListener('click', () => this.togglePause());
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) this.start(); });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); else this.start(); });

      this.goTo(this.current, false);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    goTo(index, userInitiated = false) {
      if (!this.slides?.length) return;
      const normalized = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalized;
        slide.toggleAttribute('hidden', !active);
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === normalized) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.current = normalized;
      if (userInitiated && this.status) this.status.textContent = `${this.slides[normalized].dataset.productTitle || 'Product'} shown`;
      if (userInitiated) {
        this.stop();
        this.start();
      }
    }

    start() {
      if (this.dataset.autoplay !== 'true' || this.reducedMotion || this.userPaused || document.hidden || this.slides?.length < 2) return;
      this.stop();
      this.timer = window.setInterval(() => this.goTo(this.current + 1, false), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      if (this.pauseButton) {
        this.pauseButton.textContent = this.userPaused ? '▶' : 'Ⅱ';
        this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Resume product rotation' : 'Pause product rotation');
      }
      if (this.userPaused) this.stop(); else this.start();
    }
  }

  if (!customElements.get('product-hero-carousel')) customElements.define('product-hero-carousel', ProductHeroCarousel);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const documentFragment = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = documentFragment.querySelector('product-recommendations');
          if (recommendations?.innerHTML.trim()) {
            this.innerHTML = recommendations.innerHTML;
            this.initRecommendationRail();
          }
        })
        .catch(() => {});
    }

    initRecommendationRail() {
      const rail = this.querySelector('[data-recommendations-rail]');
      const previous = this.querySelector('[data-recommendations-prev]');
      const next = this.querySelector('[data-recommendations-next]');
      if (!rail) return;

      const scrollAmount = () => Math.max(rail.clientWidth * 0.78, 280);
      previous?.addEventListener('click', () => rail.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
      next?.addEventListener('click', () => rail.scrollBy({ left: scrollAmount(), behavior: 'smooth' }));
    }
  }

  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  const closeFilters = () => {
    const panel = document.querySelector('[data-filter-panel]');
    const toggle = document.querySelector('[data-filter-toggle]');
    panel?.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('filter-open');
    toggle?.focus();
  };

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton) {
      const selector = quantityButton.closest('.quantity-selector');
      const input = selector?.querySelector('[data-quantity-input]');
      if (!input) return;
      if (quantityButton.matches('[data-quantity-plus]')) input.stepUp();
      else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
      announce(`Quantity ${input.value}`);
      return;
    }

    const galleryButton = event.target.closest('[data-media-target]');
    if (galleryButton) {
      const gallery = galleryButton.closest('[data-product-gallery]');
      gallery?.querySelectorAll('[data-media-target]').forEach((button) => button.classList.toggle('is-active', button === galleryButton));
      gallery?.querySelectorAll('[data-media-id]').forEach((media) => {
        const active = media.dataset.mediaId === galleryButton.dataset.mediaTarget;
        media.toggleAttribute('hidden', !active);
        media.classList.toggle('is-active', active);
      });
      return;
    }

    const filterToggle = event.target.closest('[data-filter-toggle]');
    if (filterToggle) {
      const panel = document.querySelector('[data-filter-panel]');
      panel?.classList.add('is-open');
      filterToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('filter-open');
      panel?.querySelector('[data-filter-close]')?.focus();
      return;
    }

    if (event.target.closest('[data-filter-close]')) closeFilters();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('filter-open')) closeFilters();
  });

  document.addEventListener('change', (event) => {
    const hanbangChoice = event.target.closest('[data-hanbang-choice]');
    if (hanbangChoice) {
      const root = hanbangChoice.closest('[data-hanbang-bundle-root]');
      const price = hanbangChoice.dataset.choicePrice || '$99';
      const available = hanbangChoice.dataset.choiceAvailable === 'true';
      const choiceName = hanbangChoice.dataset.choiceName || 'Selected cleanser';
      root?.querySelectorAll('.hanbang-bundle__choice').forEach((label) => label.classList.toggle('is-selected', label.contains(hanbangChoice)));
      root?.querySelectorAll('[data-hanbang-price], [data-hanbang-button-price]').forEach((element) => { element.textContent = price; });
      const selection = root?.querySelector('[data-hanbang-selection]');
      const submit = root?.querySelector('[data-hanbang-submit]');
      if (selection) selection.textContent = choiceName;
      if (submit) submit.disabled = !available;
      announce(`${choiceName} selected for the Complete Hanbang Starter Ritual. ${price}.`);
      return;
    }

    const sortSelect = event.target.closest('[data-sort-select]');
    if (sortSelect) {
      sortSelect.form?.submit();
      return;
    }

    const variantSelect = event.target.closest('[data-variant-select]');
    if (!variantSelect) return;
    const root = variantSelect.closest('[data-product-root]');
    const option = variantSelect.options[variantSelect.selectedIndex];
    const available = option.dataset.available === 'true';
    const price = option.dataset.price || '';
    const comparePrice = option.dataset.comparePrice || '';
    const priceRaw = Number(option.dataset.priceRaw || 0);
    const comparePriceRaw = Number(option.dataset.comparePriceRaw || 0);
    const savingsPercent = comparePriceRaw > priceRaw ? Math.round(((comparePriceRaw - priceRaw) / comparePriceRaw) * 100) : 0;
    const onSale = comparePriceRaw > priceRaw;
    const priceWrap = root?.querySelector('[data-price-wrap]');
    const currentPrice = root?.querySelector('[data-product-price]');
    const compare = root?.querySelector('[data-compare-price]');
    const saveBadge = root?.querySelector('[data-save-badge]');
    const submit = root?.querySelector('[data-add-to-cart]');
    const submitText = root?.querySelector('[data-add-to-cart-text]');
    const buttonPrice = root?.querySelector('[data-button-price]');
    const stickyPrice = root?.querySelector('[data-sticky-price]');
    const stickyButton = root?.querySelector('[data-sticky-add]');
    const stickyNotify = root?.querySelector('[data-sticky-notify]');
    const backInStock = root?.querySelector('[data-back-in-stock]');
    const backInStockTags = root?.querySelector('[data-back-in-stock-tags]');

    if (currentPrice) currentPrice.textContent = price;
    if (compare) {
      compare.textContent = comparePrice;
      compare.hidden = !onSale;
    }
    if (priceWrap) priceWrap.classList.toggle('price--sale', onSale);
    if (saveBadge) {
      saveBadge.hidden = savingsPercent < 1;
      if (savingsPercent > 0) saveBadge.textContent = `Save ${savingsPercent}%`;
    }
    if (buttonPrice) buttonPrice.textContent = price;
    if (stickyPrice) stickyPrice.textContent = price;
    if (submit) submit.disabled = !available;
    if (stickyButton) stickyButton.hidden = !available;
    if (stickyNotify) stickyNotify.hidden = available;
    if (backInStock) backInStock.hidden = available;
    if (backInStockTags) backInStockTags.value = `${backInStockTags.dataset.baseTags},variant:${option.value}`;
    if (submitText) submitText.textContent = available ? 'Add to bag' : 'Sold out';
    announce(`${option.textContent.trim()} selected. ${available ? price : 'Sold out'}`);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.quick-add-form');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const originalText = button?.textContent || 'Add to bag';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error('Unable to add product');
      const item = await response.json();
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('is-empty', cart.item_count === 0);
      });
      if (button) button.textContent = 'Added ✓';
      announce(`${item.product_title || 'Product'} added to your bag.`);
      window.setTimeout(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      }, 1800);
    } catch (error) {
      if (button) {
        button.disabled = false;
        button.textContent = 'Try again';
      }
      announce('That item could not be added. Please try again.');
    }
  });

  class RoutineSwitcher extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-routine-mode]'));
      this.content = Array.from(this.querySelectorAll('[data-routine-copy]'));
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.select(button.dataset.routineMode, true));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const current = this.buttons.indexOf(button);
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const next = this.buttons[(current + direction + this.buttons.length) % this.buttons.length];
          next.focus();
          this.select(next.dataset.routineMode, true);
        });
      });
      const selected = this.buttons.find((button) => button.getAttribute('aria-selected') === 'true');
      this.select(selected?.dataset.routineMode || 'am', false);
    }

    select(mode, shouldAnnounce) {
      this.buttons.forEach((button) => {
        const active = button.dataset.routineMode === mode;
        button.setAttribute('aria-selected', String(active));
        button.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.content.forEach((panel) => {
        panel.hidden = panel.dataset.routineCopy !== mode;
      });
      if (shouldAnnounce) announce(`${mode === 'am' ? 'Morning' : 'Night'} routine selected.`);
    }
  }

  if (!customElements.get('routine-switcher')) customElements.define('routine-switcher', RoutineSwitcher);

  class SkinConcernMap extends HTMLElement {
    connectedCallback() {
      this.points = Array.from(this.querySelectorAll('[data-concern-point]'));
      this.panels = Array.from(this.querySelectorAll('[data-concern-panel]'));
      this.points.forEach((point) => point.addEventListener('click', () => this.select(point.dataset.concernPoint, true)));
      const selected = this.points.find((point) => point.getAttribute('aria-pressed') === 'true');
      this.select(selected?.dataset.concernPoint || this.points[0]?.dataset.concernPoint, false);
    }

    select(concern, shouldAnnounce) {
      if (!concern) return;
      this.points.forEach((point) => point.setAttribute('aria-pressed', String(point.dataset.concernPoint === concern)));
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.concernPanel !== concern; });
      const activePanel = this.panels.find((panel) => panel.dataset.concernPanel === concern);
      if (shouldAnnounce && activePanel) announce(`${activePanel.querySelector('h3')?.textContent || 'Skin concern'} selected.`);
    }
  }

  if (!customElements.get('skin-concern-map')) customElements.define('skin-concern-map', SkinConcernMap);

  class BeforeAfterSlider extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-before-after-range]');
      if (!this.range) return;
      this.update();
      this.range.addEventListener('input', () => this.update());
      this.range.addEventListener('change', () => announce(`Comparison divider set to ${this.range.value} percent.`));
    }

    update() {
      const value = Math.min(90, Math.max(10, Number(this.range?.value) || 50));
      this.style.setProperty('--ba-split', `${value}%`);
    }
  }

  if (!customElements.get('before-after-slider')) customElements.define('before-after-slider', BeforeAfterSlider);

  const rememberCampaignContext = () => {
    const returnKey = 'beautyraise_campaign_return';
    const nameKey = 'beautyraise_campaign_name';
    const catalogPath = '/collections/beautyraise-campaign-products';
    const utilityPath = /^\/(collections|products|search|cart|checkouts)(\/|$)/;
    const pageText = document.querySelector('main')?.textContent || '';
    const hasFundletBlock = Array.from(document.querySelectorAll('.shopify-app-block, [class*="fundlet" i], [id*="fundlet" i]')).some((element) => {
      const text = element.textContent || '';
      return /raised\s+of|fundraiser|campaign/i.test(text);
    });
    const isCampaignOrigin = Boolean(
      document.querySelector('[data-campaign-origin], .br-campaign-hero, .br-attribution-note') ||
      hasFundletBlock ||
      (/raised\s+of/i.test(pageText) && /fundraiser|campaign/i.test(pageText))
    );

    try {
      if (isCampaignOrigin && !utilityPath.test(window.location.pathname) && window.location.pathname !== catalogPath) {
        const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const campaignHeading = document.querySelector('main h1, .shopify-app-block h1, .shopify-app-block h2')?.textContent?.trim();
        window.sessionStorage.setItem(returnKey, returnUrl);
        if (campaignHeading) window.sessionStorage.setItem(nameKey, campaignHeading.slice(0, 100));
      }

      const returnUrl = window.sessionStorage.getItem(returnKey);
      const campaignName = window.sessionStorage.getItem(nameKey);
      if (!returnUrl || returnUrl === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;

      document.querySelectorAll('[data-campaign-return]').forEach((link) => {
        link.href = returnUrl;
        link.hidden = false;
        link.setAttribute('aria-label', campaignName ? `Back to ${campaignName}` : 'Back to fundraiser');
      });
    } catch (error) {
      // Campaign tracking still remains with Fundlet and Shopify if session storage is unavailable.
    }
  };

  rememberCampaignContext();

  const mainAddButton = document.querySelector('[data-add-to-cart]');
  const stickyBar = document.querySelector('[data-mobile-sticky]');
  if (mainAddButton && stickyBar && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      const passedButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      stickyBar.classList.toggle('is-visible', passedButton);
    }, { threshold: 0 });
    observer.observe(mainAddButton);
  }
})();
